// The `tasks` status machine and the sets derived from it, plus the coarse
// lifecycle bucket a watcher reads. See `../task.ts` for the file docblock.

import { z } from 'zod';

export const TaskStatus = {
  /** Minted, not yet claimed by a runner. */
  Pending: 'pending',
  /** Claimed for one runner by the pull-assigner; runner hasn't started. */
  Assigned: 'assigned',
  /** The runner is driving the coding loop. */
  InProgress: 'in_progress',
  /** Work landed as a PR; the babysit watch (doc 04) takes over from here. */
  Completed: 'completed',
  /** Babysit watch: PR open, CI not yet green — a red PR being self-fixed, or checks pending. */
  AwaitingCi: 'awaiting_ci',
  /** Babysit watch: CI green, gating on review / comments / mergeability. */
  AwaitingReview: 'awaiting_review',
  /** Babysit watch: all gates green — ready to merge (auto-merge fires, or a human does). */
  Mergeable: 'mergeable',
  /** The PR merged. Terminal (happy path). */
  Merged: 'merged',
  /** The runner errored out or gave up. Terminal. */
  Failed: 'failed',
  /** Operator- or policy-cancelled. Terminal. */
  Cancelled: 'cancelled',
  /**
   * Paused by a person at a safe point. The run ended at a step boundary; what it could
   * save is on the task branch, and the task waits on its machine for Resume. NOT
   * terminal. Its machine is `tasks.pinned_runner_id`; `runner_id` stays null, so no
   * box-held reader (`BOX_HELD_TASK_STATUSES`) treats it as held. Written by the box's park
   * on release (#6748) and the pause door's not-yet-started arm (#6751); #6747 is the
   * reader half. Never roll a service back past #6747: an older build cannot parse it
   * (`docs/idea/releases.md` § rollback).
   */
  Paused: 'paused',
} as const;
export const TaskStatusSchema = z.enum(TaskStatus);
export type TaskStatus = z.infer<typeof TaskStatusSchema>;

/**
 * A dependency is satisfied — and stops blocking its dependents — once it
 * reaches one of these. `claimNextReady` derives its readiness SQL from this
 * set, so the queue and the domain agree on "done enough to unblock".
 */
export const TASK_DEP_SATISFIED_STATUSES = [TaskStatus.Completed, TaskStatus.Merged] as const;

/**
 * Terminal states. A row leaves one in exactly two ways:
 *
 *  - a REVIVE back to `pending` — every revive writer is enumerated at
 *    `KEY_LABEL_READING_TASK_STATUSES` (`packages/db/src/queries/byok/lifecycle.ts`);
 *  - ONE terminal-to-terminal hop, `failed → merged`, written only by the settled-PR
 *    sweep (`listTasksOnSettledPullRequests` → `apps/worker/src/cron/pr-settled-sweep.ts`)
 *    for a CODING row whose PR MERGED after its run failed (#4022). The merge outranks
 *    how the run ended; the row keeps its failure columns.
 *
 * Every other status write in `packages/db` fences terminal rows out of its own CAS;
 * `transitionTask` CASes on its caller's `from`, and the sweep is the one caller that
 * passes a terminal one.
 */
export const TERMINAL_TASK_STATUSES = [
  TaskStatus.Merged,
  TaskStatus.Failed,
  TaskStatus.Cancelled,
] as const;

/**
 * Is this repo-onboarding (`setup`-origin) pass DEAD — finished, with nothing left that
 * could stamp `repositories.ai_first_at` and release the repo (#6081)?
 *
 *  - {@link TERMINAL_TASK_STATUSES}: dead whichever one it holds — the one hop between
 *    them (`failed → merged`) stays terminal, and only a revive makes a pass live again.
 *    `merged` reaches a caller only when a maintainer cleared the marker after the pass
 *    landed — asking to be onboarded again.
 *  - `completed` with NO pull request. A `gaps` pass completes with its `dz/onboarding`
 *    PR linked and that PR's merge stamps the marker; with no PR there is nothing left to
 *    merge (pre-#3489 `blocked` passes that settled `completed`, a proof-less `ready`).
 *
 * Every other status is live: pending / assigned / in_progress will finish, and the
 * PR-watch statuses (`awaiting_*`, `mergeable`) end in a merge that stamps.
 *
 * The SQL half is `noLiveOnboardingPassSql` (`packages/db/src/queries/tasks/onboarding-owed.ts`);
 * `apps/worker/src/cron/onboarding-backfill.dead-rule.integration.test.ts` seeds every
 * status × PR null/non-null and asserts the two agree.
 */
export function isDeadOnboardingPass(pass: {
  readonly status: TaskStatus;
  readonly pullRequestNumber: number | null;
}): boolean {
  if ((TERMINAL_TASK_STATUSES as readonly TaskStatus[]).includes(pass.status)) return true;
  return pass.status === TaskStatus.Completed && pass.pullRequestNumber === null;
}
/**
 * The mirror of {@link TASK_DEP_SATISFIED_STATUSES}: dependency statuses that are
 * TERMINAL and not satisfying. A pending task whose `depends_on` names one of these
 * is not claimable — the claim gate admits a dependent only when every dependency is
 * in the satisfied set — and stays so unless that dependency is revived or takes the
 * one late hop below ({@link TERMINAL_TASK_STATUSES} enumerates both).
 *
 * Derived from the two sets rather than written out, so this stays correct if a
 * terminal status is added (dead by default) or if a status is later promoted to
 * "done enough to unblock" (satisfying, not dead). Writing `[failed, cancelled]`
 * by hand would be a third copy of a rule two constants already state.
 *
 * Why this needs a name at all: without a sweep keyed on it, a failed dependency
 * deadlocks its dependents SILENTLY and forever. Observed 2026-08-23 on
 * `developerz-ai/developerz.ai` — 85 tasks pending up to eleven days behind 64
 * failed dependencies, `hold_reason` null on every one, five boxes idle, and the
 * fleet reporting a deep queue that read as the #513 same-area gate. Attempts do
 * not resume, so no retry ever satisfies a failed dependency.
 *
 * THE ONE EXCEPTION (#4022): a `failed` CODING dependency whose PR then MERGES is
 * moved to `merged` by the settled-PR sweep ({@link TERMINAL_TASK_STATUSES}), and so
 * becomes satisfying. A dependent still `pending` at that moment is released by the
 * claim gate as usual; one the dead-dependency sweep already failed
 * (`dependency_failed`, terminal) stays failed — nothing automatic revisits it, and
 * only a revive ({@link TERMINAL_TASK_STATUSES}) runs it.
 */
export const TASK_DEP_UNSATISFIABLE_STATUSES: readonly TaskStatus[] = TERMINAL_TASK_STATUSES.filter(
  (s) => !(TASK_DEP_SATISFIED_STATUSES as readonly TaskStatus[]).includes(s),
);

/**
 * The #1913 orphan set: every terminal status except `merged` (`failed` /
 * `cancelled` today). A task here died holding its PR — the run is over, the row
 * never re-enters the watch on its own (`canEnterBabysit` admits no terminal status;
 * the one way back is the revive of a watch ANCHOR the bot cancelled `abandoned`, for
 * a PR a person reopened, #7709, `apps/worker/src/babysit/watch-anchor.ts`), and
 * nobody is driving the PR. Deliberately NOT the wider "anything `canEnterBabysit`
 * refuses": a box-held owner (`pending`/`assigned`/`in_progress`) still has a LIVE
 * run that owns the PR, and a `merged` owner needs no watch at all.
 *
 * Derived from {@link TERMINAL_TASK_STATUSES} rather than a hand-written pair, so a
 * terminal status added later is a dead owner here by default instead of a silent
 * miss. Lives in the domain because THREE seams must agree about what a dead owner
 * is and they cannot all import each other: the babysit driver's webhook mint guard
 * (`apps/worker/src/babysit/driver.ts`), the wiring's audit re-read
 * (`apps/worker/src/babysit/wiring.ts`), and the backfill enumeration
 * (`listDeadOwnerOrphanPrs`, `packages/db`) — `packages/db` cannot import
 * `apps/worker`, so a db-side copy would be the drift #1959 removed, re-introduced.
 */
export const DEAD_OWNER_STATUSES: ReadonlySet<TaskStatus> = new Set(
  TERMINAL_TASK_STATUSES.filter((s) => s !== TaskStatus.Merged),
);

/**
 * Coarse lifecycle bucket for operator/fan-out visibility (`task_list`,
 * `fleet_status`, the space agent monitoring a fan-out). Collapses the fine
 * `TaskStatus` machine into the four states a watcher cares about: is it still
 * WAITING for capacity (`queued`), RUNNING on a box, done (`succeeded`), or dead
 * (`failed`). This is a read-model projection — the durable status stays the
 * authority; nothing dispatches off a bucket.
 */
export const TaskLifecycleBucket = {
  /** `pending` — enqueued, waiting for a runner to free (the queue). */
  Queued: 'queued',
  /** Claimed / executing / babysit-watching an open PR — work is in flight. */
  Running: 'running',
  /** Coding landed (`completed`) or the PR merged — the happy terminal set. */
  Succeeded: 'succeeded',
  /** The runner errored/gave up (`failed`) or it was cancelled — dead. */
  Failed: 'failed',
} as const;
export const TaskLifecycleBucketSchema = z.enum(TaskLifecycleBucket);
export type TaskLifecycleBucket = z.infer<typeof TaskLifecycleBucketSchema>;

/**
 * Project a `TaskStatus` onto its {@link TaskLifecycleBucket}. `succeeded` is the
 * `TASK_DEP_SATISFIED_STATUSES` set (`completed`/`merged` — "done enough"), so the
 * bucket and the DAG-readiness gate never disagree on what "done" means. Total
 * over the enum (a `switch` with no default) — a new status fails typecheck here
 * until it's bucketed, so visibility can never silently drop a state.
 */
export function taskLifecycleBucket(status: TaskStatus): TaskLifecycleBucket {
  switch (status) {
    case TaskStatus.Pending:
      return TaskLifecycleBucket.Queued;
    case TaskStatus.Assigned:
    case TaskStatus.InProgress:
    case TaskStatus.AwaitingCi:
    case TaskStatus.AwaitingReview:
    case TaskStatus.Mergeable:
    // #6747: in flight and holding its machine; not waiting for capacity, not done.
    // `UNCLAIMED_TASK_STATUSES` (derived from this bucket) stays free of paused rows.
    case TaskStatus.Paused:
      return TaskLifecycleBucket.Running;
    case TaskStatus.Completed:
    case TaskStatus.Merged:
      return TaskLifecycleBucket.Succeeded;
    case TaskStatus.Failed:
    case TaskStatus.Cancelled:
      return TaskLifecycleBucket.Failed;
  }
}
